import { Link as FluentLink, Body1Strong, MessageBar, MessageBarBody } from '@fluentui/react-components';
import { ArrowSyncRegular } from '@fluentui/react-icons';
import { useContext, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  BindingModeBadge,
  ButtonLoading,
  ConfirmDialog,
  ControlledTooltip,
  TConfirmDialogState,
} from '@shared/components';
import { useSearchParams } from '@shared/hooks';
import { TERMINOLOGY } from '@shared/i18n';
import { buildHref } from '@shared/utils';

import { SignInContext } from '@modules/users';
import { DocumentContext, TDocument, TDraft, TVersion, useTemplateUpdate, hasTemplateSource } from '@modules/documents';

import { TPermissions } from '@modules/access/types';
import { useCanEdit } from '@modules/access/hooks/useCanEdit.hook';
import splitExtension from '../../helpers/splitExtension.helper';

type TProps = {
  item: TDocument;
  version: TDraft | TVersion | undefined;
  permissions: TPermissions;
};

const TemplateInfo = ({ item, version, permissions }: TProps) => {
  const { organization } = useContext(SignInContext);
  const documentContext = useContext(DocumentContext);
  const { change } = useSearchParams();
  const { canEdit } = useCanEdit(permissions.accessRight);

  // Label
  const templateId = item.templateId;
  const templateResolvedName = templateId ? documentContext?.resolvedItemNames?.[templateId] : undefined;
  const [templateNameWithoutExt] = templateResolvedName ? splitExtension(templateResolvedName) : [''];
  const templateBaseName = templateNameWithoutExt || templateResolvedName || `Unknown ${TERMINOLOGY.template.lower}`;
  const templateVersionId = version?.templateSource?.versionId;
  const templateLabel = templateId
    ? `${templateBaseName}${templateVersionId ? ` (v${templateVersionId})` : ''}`
    : undefined;

  // Update detection - uses draft or latest version (not viewed version, which may be older via ?view= param)
  const templateUpdate = useTemplateUpdate();
  const latestDocVersion = item.versions[0] as TVersion | undefined;
  const currentTemplateSource = item.draft?.templateSource ?? latestDocVersion?.templateSource;
  const templateApprovedVersionId = item.templateBlobInfo?.approvedVersionId;
  const isBoundToTemplate = !!currentTemplateSource?.fieldValuesBlobName;
  const templateBindingEnabled = organization.featureSettings.templateBinding?.enabled;
  const isUpdateAvailable =
    templateBindingEnabled &&
    !!currentTemplateSource?.versionId &&
    !!templateApprovedVersionId &&
    currentTemplateSource.versionId !== templateApprovedVersionId;
  const hasDraft = !!item.draft;
  const isTemplateUnavailable = !templateResolvedName;

  const [templateUpdateDialogState, setTemplateUpdateDialogState] = useState<TConfirmDialogState | undefined>(
    undefined
  );

  if (!templateId || !templateLabel || !hasTemplateSource(version)) return null;

  return (
    <>
      <ConfirmDialog state={templateUpdateDialogState} setState={setTemplateUpdateDialogState} />
      <div className="flex">
        <span className="block w-32 flex-shrink-0">{TERMINOLOGY.template.upper}:</span>
        <div className="flex-col space-y-2">
          <Link
            to={buildHref(`/document/${templateId}`, templateVersionId ? { view: templateVersionId } : {})}
            target="_blank"
            rel="noopener noreferrer"
            className="block"
          >
            <FluentLink>
              <Body1Strong className="force-break-word">{templateLabel}</Body1Strong>
            </FluentLink>
          </Link>
          {templateBindingEnabled && currentTemplateSource && <BindingModeBadge isBound={isBoundToTemplate} />}
          {isUpdateAvailable && (
            <ControlledTooltip
              content={
                isTemplateUnavailable
                  ? `The ${TERMINOLOGY.template.lower} is no longer available.`
                  : hasDraft
                    ? 'Complete the open draft first.'
                    : !canEdit
                      ? 'You do not have permission to perform this action.'
                      : ''
              }
              relationship="label"
              withArrow
            >
              <ButtonLoading
                size="small"
                appearance="outline"
                icon={<ArrowSyncRegular />}
                disabled={isTemplateUnavailable || hasDraft || !canEdit}
                onClick={() => {
                  const dialogState: TConfirmDialogState = {
                    isOpen: true,
                    title: `Update ${TERMINOLOGY.template.lower}?`,
                    content: isBoundToTemplate ? (
                      <div className="space-y-3">
                        <MessageBar intent="warning">
                          <MessageBarBody>
                            The {TERMINOLOGY.template.lower} structure will be updated. Your field values will be kept.
                            New fields will use their default values.
                          </MessageBarBody>
                        </MessageBar>
                        <p>A new draft will be created based on the latest {TERMINOLOGY.template.lower} version.</p>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <MessageBar intent="warning">
                          <MessageBarBody>
                            The {TERMINOLOGY.template.lower} structure will be updated. Your field values will be
                            preserved. Changes made outside of fields will be lost.
                          </MessageBarBody>
                        </MessageBar>
                        <p>A new draft will be created from the latest {TERMINOLOGY.template.lower} version.</p>
                      </div>
                    ),
                    buttonLabel: 'Update',
                    onConfirm: async () => {
                      try {
                        await templateUpdate.mutateAsync({ id: item._id });
                        change('view', 'draft');
                      } catch {
                        /* handled by mutation onError */
                      }
                      setTemplateUpdateDialogState({ ...dialogState, isOpen: false });
                    },
                  };
                  setTemplateUpdateDialogState(dialogState);
                }}
              >
                Update to v{templateApprovedVersionId}
              </ButtonLoading>
            </ControlledTooltip>
          )}
        </div>
      </div>
    </>
  );
};

export default TemplateInfo;
